import type {
  PanelScreenshotFailure,
  PanelScreenshotSource,
} from '../../../../services/analytics/agentChatEvents';
import {generatePNGDataURL} from '../../../../util/panelExport';
import type {DraftHandle} from '../../../chat/composer';
import {
  fileToImagePart,
  MAX_IMAGE_BYTES,
  MAX_IMAGES_PER_TURN,
  MAX_TOTAL_IMAGE_BYTES,
  toComposerImage,
  validateImageParts,
} from '../../../chat/composer/imageParts';

export class PanelScreenshotError extends Error {
  constructor(
    readonly code: PanelScreenshotFailure,
    message: string
  ) {
    super(message);
  }
}

export async function renderPanelImage({
  panel,
  kind,
  source,
}: {
  panel: HTMLElement;
  kind: string;
  source: PanelScreenshotSource;
}) {
  if (panel.querySelector('.ui.active.loader, [data-test^="wave-loader-"]')) {
    throw new PanelScreenshotError(
      'loading',
      'Wait for the panel to finish loading, then try again.'
    );
  }
  await document.fonts?.ready;
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  const {width, height} = panel.getBoundingClientRect();
  if (width <= 0 || height <= 0) {
    throw new PanelScreenshotError(
      'capture-failed',
      'This panel is not ready to capture. Try again.'
    );
  }
  const dataUrl = await generatePNGDataURL(panel, {
    zoomFactor: Math.min(window.devicePixelRatio || 1, 2),
    backgroundColor: getComputedStyle(panel).backgroundColor,
  });
  // oxlint-disable-next-line wandb/no-unprefixed-urls -- This is an in-memory PNG data URL, not a backend URL.
  const blob = await (await fetch(dataUrl)).blob();
  const result = await fileToImagePart(
    new File([blob], 'panel-screenshot.png', {type: 'image/png'})
  );
  if (!result.ok) {
    throw new PanelScreenshotError(
      result.reason === 'too-large' ? 'over-limit' : 'capture-failed',
      result.reason === 'too-large'
        ? `This screenshot exceeds the ${MAX_IMAGE_BYTES / (1024 * 1024)} MiB image limit.`
        : 'Could not capture this panel. Try again.'
    );
  }
  if (!panel.isConnected) {
    return;
  }
  const image = {
    ...toComposerImage(result.part),
    screenshotKind: kind,
    screenshotSource: source,
  };
  return {image, bytes: blob.size};
}

export async function capturePanelImage({
  addFiles,
  ...options
}: Parameters<typeof renderPanelImage>[0] & {
  addFiles: DraftHandle['addFiles'];
}) {
  const result = await renderPanelImage(options);
  if (!result) {
    return;
  }
  const {image, bytes} = result;
  let rejection: 'too-many' | 'total-too-large' | undefined;
  const attached = addFiles([image], next => {
    const validation = validateImageParts(next);
    if (!validation.ok) {
      rejection = validation.reason;
    }
    return validation.ok;
  });
  if (!attached && rejection) {
    throw new PanelScreenshotError(
      'over-limit',
      rejection === 'too-many'
        ? `You can attach up to ${MAX_IMAGES_PER_TURN} images.`
        : `Attachments exceed the ${MAX_TOTAL_IMAGE_BYTES / (1024 * 1024)} MiB total limit.`
    );
  }
  return attached ? {attachmentId: image.id, bytes} : undefined;
}
